import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  Calendar,
  Globe,
  MapPin,
  Pencil,
  Search,
  Shield,
  UserPlus,
  X,
} from "lucide-react"
import { useState } from "react"

import ContentActionMenu from "@/components/content-action-menu"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import { searchClubMembersPath } from "@/routes"
import type { ClubsShow } from "@/types/serializers"

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  scout: "Scout",
  coach: "Coach",
  staff: "Staff",
}

function formatWebsiteDisplay(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "")
}

interface SearchResult {
  id: number
  user: {
    id: number
    first_name: string
    last_name: string
    full_name: string
    email: string
  }
  organization_name: string | null
}

export default function ClubShow({ club, members, is_own_profile }: ClubsShow) {
  const { auth } = usePage<{ auth: { user: { id: number } | null } }>().props
  const isClubAdmin = auth?.user?.id === club.user.id

  const [showAddMember, setShowAddMember] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [selectedScout, setSelectedScout] = useState<SearchResult | null>(null)
  const [searching, setSearching] = useState(false)
  const [adding, setAdding] = useState(false)

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (!searchQuery.trim()) return

    setSearching(true)
    fetch(
      `${searchClubMembersPath(club.slug)}?q=${encodeURIComponent(searchQuery)}`,
      { credentials: "same-origin" },
    )
      .then((res) => res.json())
      .then((data) => {
        setSearchResults(data.scouts || [])
      })
      .catch(() => {
        setSearchResults([])
      })
      .finally(() => {
        setSearching(false)
      })
  }

  function handleAddMember() {
    if (!selectedScout) return

    router.post(
      `/clubes/${club.slug}/members`,
      {
        user_id: selectedScout.user.id,
        role: "scout",
      },
      {
        onStart: () => setAdding(true),
        onFinish: () => {
          setAdding(false)
          setShowAddMember(false)
        },
      },
    )
  }

  function openAddMember() {
    setShowAddMember(true)
    setSearchQuery("")
    setSearchResults([])
    setSelectedScout(null)
  }

  const pageTitle = `${club.name} | KrownFT`

  return (
    <AppLayout>
      <Head title={pageTitle} />

      <div className="min-h-svh">
        <div className="from-krown-200 to-krown-800 bg-gradient-to-br">
          <div className="mx-auto max-w-4xl px-6 pt-12 pb-8">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-end">
              <Avatar className="border-background h-[120px] w-[120px] shrink-0 border-4 shadow-lg">
                <AvatarImage
                  src={club.avatar_url ?? undefined}
                  alt={club.name}
                  className="object-cover"
                />
                <AvatarFallback className="bg-krown-600 border-background text-2xl font-bold text-white">
                  <Shield className="h-12 w-12 text-white/60" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-white">
                      {club.name}
                    </h1>
                    <p className="text-krown-50/70">@{club.slug}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {is_own_profile && (
                      <Button
                        variant="cta"
                        size="sm"
                        className="hover:bg-krown-600 hover:border-krown-600 hover:shadow-md"
                        asChild
                      >
                        <Link href="/perfil/editar">
                          <Pencil className="mr-1.5 h-4 w-4" />
                          Editar perfil
                        </Link>
                      </Button>
                    )}
                    <ContentActionMenu
                      reportableType="User"
                      reportableId={club.user.id}
                      hideableType="User"
                      hideableId={club.user.id}
                      showBlock
                      blockUserId={club.user.id}
                    />
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {club.division && (
                    <Badge
                      variant="outline"
                      className="border-krown-accent bg-krown-accent/20 text-krown-accent"
                    >
                      {club.division}
                    </Badge>
                  )}
                  <VerifiedBadge show={club.verified} />
                  {(club.city ?? club.country) && (
                    <span className="text-krown-50/80 flex items-center gap-1 text-sm">
                      <MapPin className="h-3.5 w-3.5" />
                      {[club.city, club.country].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info bar */}
        <div className="bg-card border-b shadow-sm">
          <div className="mx-auto flex max-w-4xl items-center justify-center gap-8 px-6 py-4">
            {club.founded_year && (
              <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
                <Calendar className="h-4 w-4" />
                Fundado en {club.founded_year}
              </div>
            )}
            {club.website_url && (
              <a
                href={club.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-krown-400 flex items-center gap-1.5 text-sm hover:underline"
              >
                <Globe className="h-4 w-4" />
                {formatWebsiteDisplay(club.website_url)}
              </a>
            )}
          </div>
        </div>

        {/* Content area */}
        <div className="mx-auto max-w-4xl px-6 py-8">
          {/* Description */}
          {club.description && (
            <section className="mb-8">
              <h2 className="mb-3 text-lg font-semibold">Acerca del club</h2>
              <p className="text-muted-foreground">{club.description}</p>
            </section>
          )}

          {/* Staff members */}
          <section className="mb-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Staff</h2>
              {isClubAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openAddMember}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Agregar scout
                </Button>
              )}
            </div>
            {members.length > 0 ? (
              <div className="space-y-3">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    <div className="bg-krown-800 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                      <span className="text-krown-200 text-sm font-medium">
                        {member.user.first_name[0]}
                        {member.user.last_name[0]}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{member.user.full_name}</p>
                    </div>
                    <Badge variant="secondary">
                      {ROLE_LABELS[member.role] ?? member.role}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">
                Aún no se agregaron miembros del staff
              </p>
            )}
          </section>
        </div>
      </div>

      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar scout al staff</DialogTitle>
          </DialogHeader>

          {!selectedScout ? (
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre, organización o email..."
                  className="pl-10"
                />
              </div>
              <Button
                type="submit"
                disabled={searching || !searchQuery.trim()}
                className="w-full"
              >
                {searching ? "Buscando..." : "Buscar"}
              </Button>

              {searchResults.length > 0 && (
                <div className="max-h-60 space-y-2 overflow-y-auto">
                  {searchResults.map((scout) => (
                    <button
                      key={scout.id}
                      type="button"
                      onClick={() => setSelectedScout(scout)}
                      className="hover:bg-accent flex w-full items-center gap-3 rounded-lg border p-3 text-left"
                    >
                      <div className="bg-krown-800 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                        <span className="text-krown-200 text-xs font-medium">
                          {scout.user.first_name[0]}
                          {scout.user.last_name[0]}
                        </span>
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <p className="truncate font-medium">
                          {scout.user.full_name}
                        </p>
                        {scout.organization_name && (
                          <p className="text-muted-foreground truncate text-sm">
                            {scout.organization_name}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchQuery && searchResults.length === 0 && !searching && (
                <p className="text-muted-foreground text-center text-sm">
                  No se encontraron scouts
                </p>
              )}
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="bg-krown-800 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                  <span className="text-krown-200 text-sm font-medium">
                    {selectedScout.user.first_name[0]}
                    {selectedScout.user.last_name[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium">{selectedScout.user.full_name}</p>
                  {selectedScout.organization_name && (
                    <p className="text-muted-foreground text-sm">
                      {selectedScout.organization_name}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedScout(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedScout(null)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleAddMember}
                  disabled={adding}
                  className="flex-1"
                >
                  {adding ? "Agregando..." : "Agregar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
